import { useMemo } from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { signalMono, signalTextField } from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  matchesFormat,
  readTextInputAnswer,
  readTextInputConfig,
  TextInputConfig,
  TextInputFormat
} from './textInput';
import { BlockRendererProps, questionIdOf } from './types';

/** Show the counter only once it starts to matter, not from the first keystroke. */
const COUNTER_VISIBLE_FROM = 0.8;

/**
 * What to say when a complete value does not match its format.
 *
 * A map rather than an interpolated key. Building the key from the format at runtime would compile
 * whatever it produced, so a missing or misspelt translation would ship silently and surface as a
 * raw key on a respondent's screen — the one place nobody would see it before they did.
 */
const FORMAT_HINTS: Record<TextInputFormat, TranslationKey> = {
  PLAIN: 'guestForm.text.format.PLAIN',
  DIGITS: 'guestForm.text.format.DIGITS',
  MATRICULATION: 'guestForm.text.format.MATRICULATION'
};

/**
 * What to say up front about the shape a value must have, or nothing when it has none.
 *
 * `PLAIN` returns undefined rather than its string: that entry is a single space, present so the
 * helper line can reserve its height, and printing a blank line in the head would leave a gap the
 * reader has to account for.
 */
const shapeHintOf = (config: TextInputConfig): string | undefined =>
  config.format === 'PLAIN' ? undefined : getTrans(FORMAT_HINTS[config.format]);

/**
 * `text_input@1`: one short value on one line.
 *
 * Sibling of the long-answer block, and the difference in behaviour is the format. A `DIGITS` field
 * asks the phone for the numeric keypad, which is the single largest usability difference on a mobile
 * form and costs one attribute.
 *
 * The format error is shown only once the value is COMPLETE enough to judge — never while someone is
 * mid-keystroke (catalog §7.1: neutral while incomplete, clear once a complete value is invalid,
 * never a first-time-at-submit surprise). "Complete" here means they have stopped and it still does
 * not match, which is approximated by: non-empty, and long enough that the next character could not
 * plausibly fix it. Being wrong in the lenient direction is the right way to be wrong — a field that
 * turns red on the first digit of a number is a field people abandon.
 */
export const TextInputBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readTextInputConfig(block), [block]);
  const text = readTextInputAnswer(answer)?.text ?? '';

  const questionId = questionIdOf(block);

  const trimmed = text.trim();
  // Judged only once the value could not become valid by typing one more character.
  const settled = trimmed.length > 0 && !matchesFormat(config.format, trimmed);
  const showCounter = text.length >= config.maxLength * COUNTER_VISIBLE_FROM;

  const helperText = (): string => {
    if (settled) {
      return getTrans(FORMAT_HINTS[config.format]);
    }
    if (showCounter) {
      return getTransWithParams('guestForm.text.remaining', {
        remaining: config.maxLength - text.length
      });
    }
    return ' ';
  };

  return (
    <Stack spacing={3}>
      {/*
        The shape, said BEFORE it is needed rather than only after it is broken.

        The helper line below already names the format, but only once a complete value fails it —
        which makes it a correction. A refused answer is a wasted attempt, and "digits only" costs
        two words here and saves a respondent re-typing their whole answer. The LENGTH is not said:
        the field enforces `maxLength` by refusing further characters, so announcing it in advance
        warns about a wall nobody can walk into.
      */}
      <BlockHeading block={block} locale={locale} hint={shapeHintOf(config)} />

      <TextField
        value={text}
        onChange={event => onChange({ text: event.target.value })}
        aria-labelledby={questionId}
        aria-required={block.required}
        error={settled}
        inputProps={{
          maxLength: config.maxLength,
          // The numeric keypad on a phone: one attribute, and the largest single usability
          // difference this block can make.
          inputMode: config.format === 'DIGITS' ? 'numeric' : 'text',
          'data-testid': `form-text-${block.instanceId}`
        }}
        FormHelperTextProps={{ 'aria-live': 'polite', sx: signalMono }}
        helperText={helperText()}
        sx={signalTextField}
        fullWidth
      />
    </Stack>
  );
};
